import { useEffect, useRef, useState } from "react";
import { restoreSession, logout } from "./lib/auth";
import { onUnauthorized, type SessionUser } from "./lib/api";
import { kickCopy } from "./lib/session-kick-copy";
import { usePath } from "./lib/usePath";
import Landing from "./surfaces/Landing";
import Login from "./surfaces/Login";
import DriverApp from "./surfaces/driver/DriverApp";
import DispatchHome, {
  type DispatcherRoute,
} from "./surfaces/dispatcher/DispatchHome";
import DriverList from "./surfaces/dispatcher/DriverList";
import JobForm from "./surfaces/dispatcher/JobForm";
import JobList from "./surfaces/dispatcher/JobList";
import JobDetail from "./surfaces/dispatcher/JobDetail";
import OfficeHome, {
  type OfficeRoute,
} from "./surfaces/office/OfficeHome";
import OfficeJobForm from "./surfaces/office/OfficeJobForm";
import OfficeActiveJobDetail from "./surfaces/office/OfficeActiveJobDetail";
import FinishedJobDetail from "./surfaces/office/FinishedJobDetail";
import ReviewedJobDetail from "./surfaces/office/ReviewedJobDetail";
import EngagementMap from "./surfaces/office/EngagementMap";
import QboExport from "./surfaces/office/QboExport";
import Timeclock from "./surfaces/office/Timeclock";
import Admin from "./surfaces/office/Admin";
import DispatchNfcGate from "./components/DispatchNfcGate";
import DispatcherSignOut from "./components/DispatcherSignOut";
import LunchScreen from "./components/LunchScreen";
import {
  fetchNfcRequired,
  launchAutoPunch,
  launchGatePunch,
  submitPunch,
} from "./lib/punch";
import { usePunchState } from "./lib/punch-state";
import { fireCookieFlush } from "./lib/cookie-flush";
import Toast, { type ToastData } from "./components/Toast";
import "./App.css";

interface DispatcherChromeProps {
  dispRoute: DispatcherRoute;
  navigateDispatcher: (route: DispatcherRoute) => void;
  onClockOut: () => void;
  pushToast: (message: string, kind?: "success" | "error") => void;
}

function DispatcherChrome({
  dispRoute,
  navigateDispatcher,
  onClockOut,
  pushToast,
}: DispatcherChromeProps) {
  const { state: toolbarState, latest, lunchTakenToday, refresh } = usePunchState();
  const [lunchStartedAt, setLunchStartedAt] = useState<string | null>(null);
  const [lunchInFlight, setLunchInFlight] = useState(false);

  useEffect(() => {
    if (latest?.punchType === "LUNCH_OUT") {
      setLunchStartedAt(latest.punchedAt);
    } else if (latest) {
      setLunchStartedAt(null);
    }
  }, [latest]);

  const handleLunch = async () => {
    if (lunchInFlight) return;
    setLunchInFlight(true);
    const punchType = lunchStartedAt ? "LUNCH_IN" : "LUNCH_OUT";
    try {
      const result = await submitPunch(punchType);
      if (result.ok) {
        if (punchType === "LUNCH_OUT") {
          setLunchStartedAt(result.punchedAt ?? new Date().toISOString());
        } else {
          setLunchStartedAt(null);
        }
        await refresh();
      } else {
        pushToast(result.message || "Couldn't reach the server — try again", "error");
        await refresh(); // SPEC-65: re-pull lunchTakenToday after a rejection
      }
    } finally {
      setLunchInFlight(false);
    }
  };

  return (
    <>
      {lunchStartedAt && (
        <LunchScreen lunchStartedAt={lunchStartedAt} onEndLunch={handleLunch} />
      )}
      <nav className="bottom-nav">
        <button
          type="button"
          className={toolbarState === "ON_LUNCH" ? "toolbar-lunch" : ""}
          disabled={
            lunchInFlight ||
            (lunchTakenToday && !lunchStartedAt) ||
            (toolbarState === "NO_SHIFT" && !lunchStartedAt)
          }
          title={
            toolbarState === "NO_SHIFT" && !lunchStartedAt
              ? "Clock in first"
              : lunchTakenToday && !lunchStartedAt
                ? "Lunch already taken"
                : undefined
          }
          onClick={() => void handleLunch()}
        >
          {toolbarState === "ON_LUNCH" ? "END LUNCH" : "Lunch"}
        </button>
        <button
          type="button"
          className={dispRoute.name === "home" ? "active" : ""}
          onClick={() => navigateDispatcher({ name: "home" })}
        >
          Home
        </button>
        <button
          type="button"
          className={dispRoute.name === "jobs" || dispRoute.name === "job" ? "active" : ""}
          onClick={() => navigateDispatcher({ name: "jobs" })}
        >
          Jobs
        </button>
        <button type="button" onClick={onClockOut}>Clock Out</button>
      </nav>
    </>
  );
}

export default function App() {
  const [path, setPath] = usePath();
  const [currentUser, setCurrentUser] = useState<SessionUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const [dispatchGatePassed, setDispatchGatePassed] = useState(false);
  // REVIEW-WINDOW 2026-10-28 — the dispatcher console gate (DispatchNfcGate)
  // consults NEITHER NFC flag, so it would keep demanding a tag during the
  // App Review window. While the office punch toggle is explicitly OFF, skip
  // it. Fail closed: null (probe in flight) and true both keep the gate; only
  // a resolved false bypasses. REVERT: delete this state, its effect, and the
  // `&& dispatchNfcRequired !== false` conjunct below.
  const [dispatchNfcRequired, setDispatchNfcRequired] = useState<boolean | null>(null);
  // SPEC-76 field fix 2: cookie-restored dispatcher sessions pass the
  // console gate (a vault restore means the server just re-validated the
  // device binding — the same proof the login bypass uses). One-shot ref:
  // armed by the restore path, consumed by the reset effect when the
  // arrival on /dispatch lands (the effect resets the gate while the
  // path is still /driver during restore — the ref bridges that window).
  const restoreGatePassRef = useRef(false);
  const [dispatcherSignOutOpen, setDispatcherSignOutOpen] = useState(false);

  // Dispatcher in-surface route
  const [dispRoute, setDispRoute] = useState<DispatcherRoute>({ name: "home" });

  // Office in-surface route
  const [officeRoute, setOfficeRoute] = useState<OfficeRoute>({ name: "home" });

  const [toasts, setToasts] = useState<ToastData[]>([]);
  const toastId = useRef(0);
  // SPEC-46 rev 6 §2.5: the gate-punch AbortController lives here (the
  // persistent shell) instead of in DispatchNfcGate, which unmounts in the
  // same commit that opens the console and aborted the in-flight clock-in.
  const punchAbortControllerRef = useRef<AbortController | null>(null);

  // Normalize path and redirect unknown routes to "/"
  const cleanPath = path.toLowerCase().replace(/\/+$/, "") || "/";
  useEffect(() => {
    if (
      cleanPath !== "/" &&
      cleanPath !== "/driver" &&
      cleanPath !== "/dispatch" &&
      cleanPath !== "/office"
    ) {
      setPath("/");
    }
  }, [cleanPath, setPath]);

  // SPEC-45 Reset (e): whenever path !== "/dispatch", reset dispatchGatePassed
  useEffect(() => {
    if (cleanPath !== "/dispatch") {
      setDispatchGatePassed(false);
    } else if (restoreGatePassRef.current) {
      // SPEC-76 field fix 2: arrival on /dispatch with the restore ref
      // armed = a cookie-restored (bound-device) dispatcher session —
      // pass the gate, consume the one-shot.
      restoreGatePassRef.current = false;
      setDispatchGatePassed(true);
    }
  }, [cleanPath]);

  // REVIEW-WINDOW 2026-10-28 — probe the office punch toggle so the console
  // gate below can stand down while it is off. Same probe DispatcherSignOut
  // and PunchScreen use (30s cache). A thrown probe leaves the state null,
  // which keeps the gate up (fail closed). REVERT with the state above.
  useEffect(() => {
    if (cleanPath !== "/dispatch") return;
    let cancelled = false;
    void fetchNfcRequired()
      .then((required) => {
        if (!cancelled) setDispatchNfcRequired(required);
      })
      .catch(() => {
        // Unreachable config — stay null so the gate remains enforced.
      });
    return () => {
      cancelled = true;
    };
  }, [cleanPath]);

  const pushToast = (message: string, kind: "success" | "error" = "success") => {
    const id = ++toastId.current;
    const duration = kind === "error" ? 10000 : 4000;
    setToasts((prev) => [...prev, { id, message, kind, duration }]);
  };

  // SPEC-71 §3C (BUG #2) — the ONE toast stack, hoisted to root level and
  // rendered in EVERY surface branch. Before this, the stack lived inside the
  // dispatch/office authenticated trees, so every kick path
  // (`onUnauthorized` → `setCurrentUser(null)`) unmounted it in the same
  // commit the toast was pushed — the kick message was never visible. A
  // fragment-wrapped shell puts this stack alongside whatever surface renders,
  // so it survives the remount to Login/Landing/DriverApp intact. CSS is
  // unchanged (`.toast-stack` z-index 200). DriverApp keeps its OWN stack for
  // surface-local events (coexistence is fine).
  const toastStack = (
    <div className="toast-stack">
      {toasts.map((t) => (
        <Toast
          key={t.id}
          toast={t}
          onDismiss={(id) =>
            setToasts((prev) => prev.filter((x) => x.id !== id))
          }
        />
      ))}
    </div>
  );
  const shell = (content: React.ReactNode) => (
    <>
      {content}
      {toastStack}
    </>
  );

  const handleLoggedIn = (user: SessionUser, loginScanUid?: string) => {
    // SPEC-72 Phase 2 (audit BUG #4): /dispatch + /office authenticate
    // through the SAME WKWebView shell and cookie store as /driver, and
    // /auth/login (+ webauthn/finish) Set-Cookie the same device_token this
    // SPEC exists to persist. Login.tsx's 4 session-establishing onLoggedIn
    // sites all funnel through this one handler, so one fire here covers
    // them — fire-and-forget, cannot throw (cookie-flush.ts contract).
    fireCookieFlush();
    setCurrentUser(user);
    setDispatcherSignOutOpen(false);
    if (user.role === "DISPATCHER") {
      // SPEC-76 fix (field): a login that USED the NFC gate (loginScanUid
      // present) has already proven tag presence for this session — the
      // console's SPEC-45 gate must NOT demand a second scan. Original
      // SPEC-76 scoped the console gate out and shipped two scans (login
      // tag → auto-punch → console gate tag); the PM's contract is ONE.
      // Non-scan logins (bypass, passkey) keep the gate as before.
      setDispatchGatePassed(loginScanUid != null);
      setDispRoute({ name: "home" });
      if (cleanPath !== "/dispatch") setPath("/dispatch");
    } else if (user.role === "DRIVER") {
      if (cleanPath !== "/driver") setPath("/driver");
    } else if (user.role === "OFFICE") {
      if (cleanPath !== "/office") setPath("/office");
    }

    // SPEC-76 §2.2(b) — dispatcher single-scan: a password login that used
    // the NFC gate carries the scanned tag UID; clock the dispatcher IN with
    // the SAME tag right here (the persistent shell — SPEC-46 rev 6 lesson).
    // launchAutoPunch rides submitPunch (auto Idempotency-Key — the endpoint
    // is idempotent()-guarded, audit B1) with the shared 8s abort budget, and
    // is silent on dedup/failure by design; the toast below is the ONLY
    // visible signal and fires only on punched === true (audit I2/S6).
    if (user.role === "DISPATCHER" && loginScanUid) {
      void launchAutoPunch(loginScanUid, punchAbortControllerRef).then(
        (punched) => {
          if (punched) {
            pushToast(`Clocked in at ${new Date().toLocaleTimeString()}`);
          }
        },
      );
    }
  };

  const handleLogout = () => {
    setDispatcherSignOutOpen(false);
    void logout().then(() => {
      setCurrentUser(null);
      setDispatchGatePassed(false);
      // SPEC-76 field fix 2: never let a restore-pass outlive its session.
      restoreGatePassRef.current = false;
    });
  };

  /**
   * SPEC-46 rev 6 §2.5 — the dispatcher clock-in. The gate hands us the
   * verified uid; the punch is fire-and-forget so the console opens without
   * waiting on the network. Owning the controller here (not in the gate) is
   * the fix: the gate unmounts in the same commit that opens the console and
   * aborted every in-flight clock-in in the field.
   */
  const handleGateVerified = (uid: string) => {
    launchGatePunch(uid, punchAbortControllerRef, (msg) =>
      pushToast(msg, "error"),
    );
  };

  // Boot: restore the session from the refresh cookie if present.
  useEffect(() => {
    let cancelled = false;
    restoreSession()
      .then((user) => {
        if (cancelled) return;
        setCurrentUser(user ?? null);
        if (user?.role === "DISPATCHER") {
          // SPEC-76 field fix 2/3: a cookie-restored dispatcher is a
          // bound-device return (vault cookies exist only because the
          // device is bound; /auth/refresh just re-validated the binding —
          // its last_seen advancing IS that check passing). The console
          // gate must pass like the login bypass does, not reset.
          // FIX 3: arm on ROLE ONLY — the original window.location.pathname
          // === "/dispatch" condition never armed on the iOS shell, which
          // boots /driver and reroutes (SPEC-74) — the PM reproduced it as
          // "still asking to scan". The one-shot ref is consumed by the
          // SPEC-45 reset effect on ARRIVAL at /dispatch, whichever path
          // the session restored on.
          restoreGatePassRef.current = true;
        }
        // SPEC-74 BUG #2: mirror the login routing (handleLoggedIn) on
        // restore — the iOS shell hardcodes /driver, so a restored
        // DISPATCHER/OFFICE would otherwise stay stranded on the driver
        // surface. Only redirect when stranded on /driver (a dispatcher
        // already on /dispatch keeps the dispatchGatePassed reset above).
        // DRIVER: no navigation — stays on /driver, DriverApp's gated
        // fetch runs. React batches these setStates with the ones below
        // into one commit (position within the .then is unconstrained).
        if (user?.role === "DISPATCHER" && cleanPath === "/driver") {
          setPath("/dispatch");
        } else if (user?.role === "OFFICE" && cleanPath === "/driver") {
          setPath("/office");
        }
        // SPEC-72 Phase 2 (audit BUG #4): restoreSession bootstraps via
        // /auth/refresh, which re-issues device_token on the bound branch
        // (auth.ts:1140; the !bindingRequired branch at :1079 sets auth
        // cookies with NO device cookie — a harmless no-op flush here).
        // Cold-start-never-relogin is the swipe-away scenario SPEC-72
        // targets, so re-persist on success. The DriverLogin ×4 sites stay
        // in DriverLogin (timeline ownership + the 4↔4 tripwire).
        if (user) fireCookieFlush();
        setAuthLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setCurrentUser(null);
          setAuthLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Session-expiry mid-use → clear local session and increment epoch to remount.
  // SPEC-71 §3C (BUG #2/BUG #5): a KICK (`intentional !== true`) surfaces its
  // reason as an error toast BEFORE the remount state updates; an intentional
  // logout/Clock Out stays silent. The hoisted stack below survives the commit.
  useEffect(() => {
    return onUnauthorized((reason, intentional) => {
      if (!intentional) {
        pushToast(kickCopy(reason), "error");
      }
      setCurrentUser(null);
      setDispatchGatePassed(false);
      setSessionEpoch((e) => e + 1);
    });
  }, []);

  const navigateDispatcher = (r: DispatcherRoute) => {
    setDispRoute(r);
    window.scrollTo(0, 0);
  };

  const navigateOffice = (r: OfficeRoute) => {
    setOfficeRoute(r);
    window.scrollTo(0, 0);
  };

  const handleCreated = (jobNumber: string) => {
    navigateDispatcher({ name: "jobs" });
    pushToast(`Job ${jobNumber} created.`, "success");
  };

  const handleReviewed = (jobNumber: string) => {
    pushToast(`Job ${jobNumber} marked as REVIEWED.`, "success");
  };

  const handleEdited = (jobNumber: string) => {
    pushToast(`Job ${jobNumber} updated.`, "success");
  };

  if (authLoading) {
    return shell(
      <main className="surface">
        <p className="muted">Loading…</p>
      </main>,
    );
  }

  // 1. Root marketing landing
  if (cleanPath === "/") {
    return shell(<Landing />);
  }

  // 2. Driver surface
  if (cleanPath === "/driver") {
    return shell(
      <DriverApp key={sessionEpoch} onLogout={handleLogout} />,
    );
  }

  // 3. Dispatcher surface
  if (cleanPath === "/dispatch") {
    if (!currentUser || currentUser.role !== "DISPATCHER") {
      return shell(
        <Login
          title="Dispatcher Login"
          onLoggedIn={handleLoggedIn}
        />,
      );
    } else if (!dispatchGatePassed && dispatchNfcRequired !== false) {
      return shell(
        <DispatchNfcGate
          onVerified={handleGateVerified}
          onPassed={() => setDispatchGatePassed(true)}
          onBack={() => setPath("/")}
        />,
      );
    }

    return shell(
      <>
        {dispRoute.name === "home" && (
          <DispatchHome onNavigate={navigateDispatcher} onToast={pushToast} />
        )}
        {dispRoute.name === "new-job" && (
          <JobForm
            currentUser={currentUser}
            onNavigate={navigateDispatcher}
            onCreated={handleCreated}
          />
        )}
        {dispRoute.name === "drivers" && (
          <DriverList onNavigate={navigateDispatcher} />
        )}
        {dispRoute.name === "jobs" && (
          <JobList onNavigate={navigateDispatcher} />
        )}
        {dispRoute.name === "job" && (
          <JobDetail
            id={dispRoute.id}
            onNavigate={navigateDispatcher}
            onToast={pushToast}
          />
        )}

        {dispatcherSignOutOpen && (
          <DispatcherSignOut
            onComplete={handleLogout}
            onCancel={() => setDispatcherSignOutOpen(false)}
          />
        )}

        <DispatcherChrome
          dispRoute={dispRoute}
          navigateDispatcher={navigateDispatcher}
          onClockOut={() => setDispatcherSignOutOpen(true)}
          pushToast={pushToast}
        />
      </>,
    );
  }

  // 4. Office surface
  if (cleanPath === "/office") {
    if (!currentUser || currentUser.role !== "OFFICE") {
      return shell(
        <Login
          title="Office Login"
          onLoggedIn={handleLoggedIn}
        />,
      );
    }

    return shell(
      <>
        {/* Top Desktop Navigation */}
        <header className="office-top-nav">
          <div className="office-top-nav-inner">
            <div className="office-brand" onClick={() => navigateOffice({ name: "home" })}>
              <img
                className="brand-logo"
                src="/eagle-delivery-logo.jpg"
                alt="Eagle Dispatch logo"
              />
              <strong>Eagle Dispatch</strong>
              <span className="role-tag">Office</span>
            </div>

            <nav className="office-nav-links">
              <button
                type="button"
                className={`office-nav-link ${
                  officeRoute.name === "home" ||
                  officeRoute.name === "active-job" ||
                  officeRoute.name === "finished-job" ||
                  officeRoute.name === "reviewed-job"
                    ? "active"
                    : ""
                }`}
                onClick={() => navigateOffice({ name: "home" })}
              >
                Dashboard
              </button>
              <button
                type="button"
                className={`office-nav-link ${
                  officeRoute.name === "map" ? "active" : ""
                }`}
                onClick={() => navigateOffice({ name: "map" })}
              >
                Map
              </button>
              <button
                type="button"
                className={`office-nav-link ${
                  officeRoute.name === "export" ? "active" : ""
                }`}
                onClick={() => navigateOffice({ name: "export" })}
              >
                QBO Export
              </button>
              <button
                type="button"
                className={`office-nav-link ${
                  officeRoute.name === "timeclock" ? "active" : ""
                }`}
                onClick={() => navigateOffice({ name: "timeclock" })}
              >
                Timeclock
              </button>
              <button
                type="button"
                className={`office-nav-link ${
                  officeRoute.name === "admin" ? "active" : ""
                }`}
                onClick={() => navigateOffice({ name: "admin" })}
              >
                Admin
              </button>
            </nav>

            <div className="office-nav-user">
              <span className="muted" style={{ fontSize: "13px" }}>
                {currentUser.name || currentUser.username || "Office Staff"}
              </span>
              <button
                type="button"
                className="link-button"
                style={{ fontSize: "13px" }}
                onClick={handleLogout}
              >
                Sign Out
              </button>
            </div>
          </div>
        </header>

        {officeRoute.name === "home" && (
          <OfficeHome onNavigate={navigateOffice} />
        )}
        {officeRoute.name === "map" && (
          <EngagementMap />
        )}
        {officeRoute.name === "new-job" && (
          <OfficeJobForm
            onNavigate={navigateOffice}
            onCreated={() => navigateOffice({ name: "home" })}
            onToast={pushToast}
          />
        )}
        {officeRoute.name === "active-job" && (
          <OfficeActiveJobDetail
            id={officeRoute.id}
            onNavigate={navigateOffice}
            onToast={pushToast}
          />
        )}
        {officeRoute.name === "finished-job" && (
          <FinishedJobDetail
            id={officeRoute.id}
            onNavigate={navigateOffice}
            onReviewed={handleReviewed}
          />
        )}
        {officeRoute.name === "reviewed-job" && (
          <ReviewedJobDetail
            id={officeRoute.id}
            onNavigate={navigateOffice}
            onEdited={handleEdited}
          />
        )}
        {officeRoute.name === "export" && (
          <QboExport onNavigate={navigateOffice} />
        )}
        {officeRoute.name === "timeclock" && (
          <Timeclock onNavigate={navigateOffice} onToast={pushToast} />
        )}
        {officeRoute.name === "admin" && (
          <Admin onNavigate={navigateOffice} onToast={pushToast} />
        )}
      </>,
    );
  }

  // Fallback
  return shell(<Landing />);
}
